import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { instituteTimetableApi } from '../api/timetable.api';
import { describeTimetableRange } from '../timetableRange';

export interface DeleteTimetableModalProps {
  classes: Array<{
    uuid: string;
    name: string;
    sections: Array<{ uuid: string; name: string }>;
  }>;
  initialClassUuid: string;
  initialSectionUuid: string;
  /** The timetable the page is showing, offered first when that section is chosen. */
  initialTimetableUuid: string | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Deletes one dated timetable of any section, with its lessons.
 *
 * A section can have several timetables between different dates, so the
 * office picks which one by its dates. The others are untouched — deleting
 * November's timetable must not take June's with it — and the bell times stay,
 * because they belong to the whole institute.
 */
export function DeleteTimetableModal({
  classes,
  initialClassUuid,
  initialSectionUuid,
  initialTimetableUuid,
  onClose,
  onSaved,
}: DeleteTimetableModalProps) {
  const [classUuid, setClassUuid] = useState(initialClassUuid || (classes[0]?.uuid ?? ''));
  const sections = classes.find((item) => item.uuid === classUuid)?.sections ?? [];
  const [sectionUuid, setSectionUuid] = useState(initialSectionUuid || (sections[0]?.uuid ?? ''));
  /** Empty means "whichever comes first" until the reader chooses. */
  const [chosenTimetable, setChosenTimetable] = useState(initialTimetableUuid ?? '');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data: list,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    () => (sectionUuid ? instituteTimetableApi.timetables(sectionUuid) : Promise.resolve(null)),
    [sectionUuid],
  );

  const timetables = list?.section.uuid === sectionUuid ? list.timetables : [];
  const target =
    timetables.find((timetable) => timetable.uuid === chosenTimetable) ?? timetables[0] ?? null;
  const sectionName = list?.section.displayName ?? 'this section';

  function changeClass(uuid: string) {
    setClassUuid(uuid);
    setSectionUuid(classes.find((item) => item.uuid === uuid)?.sections[0]?.uuid ?? '');
    setChosenTimetable('');
  }

  function changeSection(uuid: string) {
    setSectionUuid(uuid);
    setChosenTimetable('');
  }

  function askToConfirm(event: FormEvent) {
    event.preventDefault();
    if (target) setIsConfirming(true);
  }

  async function confirmDelete() {
    if (!target) return;

    setIsDeleting(true);
    try {
      await instituteTimetableApi.removeTimetable(target.uuid);
      toast.success(
        `${sectionName}'s timetable is deleted`,
        `${describeTimetableRange(target)}, with its ${target.lessons} ${target.lessons === 1 ? 'lesson' : 'lessons'}.`,
      );
      onSaved();
      onClose();
    } catch (caught) {
      toast.error(
        'Could not delete this timetable',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsDeleting(false);
      setIsConfirming(false);
    }
  }

  return (
    <>
      <Modal
        isOpen={!isConfirming}
        onClose={onClose}
        title="Delete a timetable"
        description="Choose the section and which of its timetables. Only that one is deleted; the section's other timetables and the bell times stay."
        size="md"
        footer={
          <>
            <Button variant="secondary" size="md" onClick={onClose} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="md"
              type="submit"
              form="delete-timetable-form"
              disabled={!target}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
            >
              Delete timetable
            </Button>
          </>
        }
      >
        <form
          id="delete-timetable-form"
          onSubmit={askToConfirm}
          className="flex flex-col gap-4 py-1 text-slate-900 dark:text-slate-100"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Class"
              options={classes.map((item) => ({ value: item.uuid, label: item.name }))}
              value={classUuid}
              onChange={(event) => changeClass(event.target.value)}
              disabled={classes.length === 0}
            />
            <Select
              label="Section"
              options={sections.map((item) => ({ value: item.uuid, label: item.name }))}
              value={sectionUuid}
              onChange={(event) => changeSection(event.target.value)}
              disabled={sections.length === 0}
            />
          </div>

          <Select
            label="Timetable"
            options={timetables.map((timetable) => ({
              value: timetable.uuid,
              label: `${describeTimetableRange(timetable)} · ${timetable.lessons} ${timetable.lessons === 1 ? 'lesson' : 'lessons'}`,
            }))}
            value={target?.uuid ?? ''}
            onChange={(event) => setChosenTimetable(event.target.value)}
            disabled={isLoading || timetables.length === 0}
            placeholder={isLoading ? 'Loading…' : 'This section has no timetable'}
            error={error ? 'Could not load this section’s timetables' : undefined}
          />
          {error && (
            <Button variant="secondary" size="sm" onClick={refetch} className="self-start">
              Try again
            </Button>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isConfirming}
        tone="danger"
        title={`Delete ${sectionName}'s timetable for ${target ? describeTimetableRange(target) : 'these dates'}?`}
        description={`Its ${target?.lessons ?? 0} ${target?.lessons === 1 ? 'lesson goes' : 'lessons go'} with it, and those dates are left without a timetable until one is created. Other timetables are not touched.`}
        confirmLabel="Yes, delete timetable"
        isLoading={isDeleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setIsConfirming(false)}
      />
    </>
  );
}
